import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {HTTP_STATUS_OK} from '../../../Constants'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {severityNotification} from '../../notification/NotificationConstant'
import {showNotification} from '../../notification/NotificationService'
import {
    countCustomPatientList,
    createOrUpdateBasicPatient,
    createOrUpdatePatient,
    dischargePatient,
    getCustomPatientList,
    getDetailPatient,
    getListPatients,
    getPagePatients,
    getUserSectionListOptions,
} from '../PatientEndPoint'

export function setStoragePatientData(id, data) {
    localStorage.setItem(`patientDetailData-${id}`, JSON.stringify(data))
}

export function getStoragePatientData(id) {
    const storagePatientData = localStorage.getItem(`patientDetailData-${id}`)

    if (!storagePatientData || storagePatientData === 'undefined') {
        return {}
    }

    try {
        return JSON.parse(storagePatientData)
    } catch {
        return {}
    }
}

export function setStoragePatientPage(hospitalId, timezoneId, data) {
    let key = 'patientPageData'
    if (hospitalId) key = `${key}-${hospitalId}`
    if (timezoneId) key = `${key}-${timezoneId}`
    localStorage.setItem(key, JSON.stringify(data))
}

export function getStoragePatientPage(hospitalId, timezoneId) {
    let key = 'patientPageData'
    if (hospitalId) key = `${key}-${hospitalId}`
    if (timezoneId) key = `${key}-${timezoneId}`

    const storagePatientPage = localStorage.getItem(key)

    if (!storagePatientPage || storagePatientPage === 'undefined') {
        return {
            content: [],
        }
    }

    try {
        return JSON.parse(storagePatientPage)
    } catch {
        return {
            content: [],
        }
    }
}

export const usePatientSectionList = (
    requestorId,
    hospitalIds = [],
    staticListIds = [],
    pathwayIds = [],
    mainRefId = null,
) => {
    const {patientKeys} = QueryKeyManager(QueryKeyTypes.patient, requestorId)
    return useQuery({
        queryKey: patientKeys.sectionList({
            hospitalIds: hospitalIds || [],
            staticListIds: staticListIds || [],
            pathwayIds: pathwayIds || [],
            mainRefId: mainRefId || null,
        }),
        queryFn: () =>
            getUserSectionListOptions(requestorId, {
                notificationHospitalIds: hospitalIds || [],
                notificationStaticListIds: staticListIds || [],
                notificationPathwayIds: pathwayIds || [],
                notificationMainRefId: mainRefId,
            }),
        enabled: !!requestorId && hospitalIds?.length > 0,
    })
}

export const usePagePatients = (
    hospitalId,
    timezoneId,
    requestorId,
    pageNumber,
    pageRows,
    sortField,
    sortOrder,
    filterRequest,
    enabled = true,
) => {
    const {patientKeys} = QueryKeyManager(QueryKeyTypes.patient, requestorId)

    return useQuery({
        queryKey: patientKeys.page({
            hospitalId,
            timezoneId,
            pageNumber,
            pageRows,
            sortField,
            sortOrder,
            filterRequest,
        }),
        queryFn: () =>
            getPagePatients(
                requestorId,
                pageNumber,
                pageRows,
                sortField,
                sortOrder,
                !!hospitalId ? {...filterRequest, hospitalId} : filterRequest,
            ),
        enabled: !!requestorId && !!enabled,
        keepPreviousData: true,
        placeholderData:
            pageNumber === 0 && pageRows === 10
                ? getStoragePatientPage(hospitalId, timezoneId)
                : {},
    })
}

export const useListPatients = (
    requestorId,
    rows,
    dto = false,
    filterRequest = {},
    enabled,
) => {
    const {patientKeys} = QueryKeyManager(QueryKeyTypes.patient, requestorId)
    return useQuery({
        queryKey: patientKeys.list({simple: dto, filterRequest}),
        queryFn: () => getListPatients(requestorId, rows, dto, filterRequest),
        enabled: !!requestorId && !!enabled,
    })
}

export const usePatientCRUD = (type, requestorId) => {
    const {patientKeys} = QueryKeyManager(QueryKeyTypes.patient, requestorId)
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: data =>
            type === QueryCRUDTypes.create || type === QueryCRUDTypes.update
                ? createOrUpdatePatient(requestorId, data)
                : data,
        onSuccess: (data, variables) => {
            // Data is the response from the server
            // Variables is the data that was passed to the mutationFn
            if (data === HTTP_STATUS_OK || data?.id) {
                if (
                    type === QueryCRUDTypes.create ||
                    type === QueryCRUDTypes.update
                ) {
                    showNotification(
                        severityNotification.success,
                        'Patient Update Successfully !!',
                    )
                }
            }
        },
    })
}

export const usePatientBasicCRUD = (type, requestorId) => {
    return useMutation({
        mutationFn: data =>
            type === QueryCRUDTypes.create || type === QueryCRUDTypes.update
                ? createOrUpdateBasicPatient(requestorId, data)
                : data,
        onSuccess: (data, variables) => {
            // Data is the response from the server
            // Variables is the data that was passed to the mutationFn
            if (data === HTTP_STATUS_OK || data?.id) {
                if (
                    type === QueryCRUDTypes.create ||
                    type === QueryCRUDTypes.update
                ) {
                    showNotification(
                        severityNotification.success,
                        'Patient Update Successfully !!',
                    )
                }
            }
        },
    })
}

export const useDetailPatient = (requestorId, id, enabled = true) => {
    const {patientKeys} = QueryKeyManager(QueryKeyTypes.patient, requestorId)
    return useQuery({
        queryKey: patientKeys.detail({id}),
        queryFn: () => getDetailPatient(requestorId, id),
        enabled: !!requestorId && !!id && !!enabled,
        placeholderData: getStoragePatientData(id) || {},
    })
}

export const useDischargePatient = (requestorId, userId) => {
    const {patientKeys} = QueryKeyManager(QueryKeyTypes.patient, requestorId)
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: data => dischargePatient(requestorId, userId, data),
        onSuccess: (data, variables) => {
            // Data is the response from the server
            // Variables is the data that was passed to the mutationFn
            if (data === HTTP_STATUS_OK || data?.id) {
                showNotification(
                    severityNotification.success,
                    'Discharge Patient Successfully !!',
                )
            }
        },
    })
}

//#Region Custom User
export const useCustomPatientList = (
    hospitalId,
    timezoneId,
    requestorId,
    pageNumber,
    pageRows,
    sortField,
    sortOrder,
    filterRequest,
    enabled = true,
) => {
    const {patientKeys} = QueryKeyManager(QueryKeyTypes.patient, requestorId)

    return useQuery({
        queryKey: patientKeys.customList({
            hospitalId,
            timezoneId,
            pageNumber,
            pageRows,
            sortField,
            sortOrder,
            filterRequest,
        }),
        queryFn: () =>
            getCustomPatientList(
                requestorId,
                pageNumber,
                pageRows,
                sortField,
                sortOrder,
                !!hospitalId ? {...filterRequest, hospitalId} : filterRequest,
            ),
        enabled: !!requestorId && !!enabled,
        keepPreviousData: true,
        // placeholderData:
        //     pageNumber === 0 && pageRows === 10
        //         ? getStoragePatientPage(hospitalId, timezoneId)
        //         : {},
    })
}

export const useCountCustomPatientList = (
    hospitalId,
    timezoneId,
    requestorId,
    filterRequest,
    enabled = true,
) => {
    const {patientKeys} = QueryKeyManager(QueryKeyTypes.patient, requestorId)

    return useQuery({
        queryKey: patientKeys.customCount({
            hospitalId,
            timezoneId,
            filterRequest,
        }),
        queryFn: () =>
            countCustomPatientList(
                requestorId,
                !!hospitalId ? {...filterRequest, hospitalId} : filterRequest,
            ),
        enabled: !!requestorId && !!enabled,
    })
}

//#EndRegion
